/**
 * UsersTab — User activity leaderboard with sortable columns.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { getAdminUserActivity, type UserActivityRow } from '../../api/client.js';

type SortDir = 'asc' | 'desc';

// ============================================================================
// Helpers
// ============================================================================

function formatTimeAgo(iso: string | null): string {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

// ============================================================================
// SortableHeader (mirrors AdminDataCachePage pattern)
// ============================================================================

interface SortableHeaderProps {
  label: string;
  field: string;
  sortField: string;
  sortDir: SortDir;
  onSort: (field: string) => void;
}

function SortableHeader({ label, field, sortField, sortDir, onSort }: SortableHeaderProps) {
  const active = sortField === field;
  return (
    <th
      role="columnheader"
      className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white select-none whitespace-nowrap"
      onClick={() => onSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        <span className="text-gray-600">
          {active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
        </span>
      </span>
    </th>
  );
}

// ============================================================================
// Spinner
// ============================================================================

function Spinner({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ============================================================================
// UsersTab
// ============================================================================

export function UsersTab() {
  const [users, setUsers] = useState<UserActivityRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState('backtestCount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAdminUserActivity();
      setUsers(result.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user activity');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const sorted = useMemo(() => {
    return [...users].sort((a, b) => {
      const av = a[sortField as keyof UserActivityRow] ?? '';
      const bv = b[sortField as keyof UserActivityRow] ?? '';
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else {
        cmp = String(av) < String(bv) ? -1 : String(av) > String(bv) ? 1 : 0;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [users, sortField, sortDir]);

  function handleSort(field: string) {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  }

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400 gap-3">
        <Spinner className="w-5 h-5" />
        <span>Loading user activity...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <p className="text-red-400">{error}</p>
        <button
          onClick={() => { void fetchUsers(); }}
          className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-200 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">{users.length} users</span>
        <button
          onClick={() => { void fetchUsers(); }}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg text-gray-200 transition-colors disabled:opacity-50"
        >
          {loading ? <Spinner /> : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
          Reload
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-gray-900 border-b border-gray-700">
            <tr>
              <SortableHeader label="Username" field="username" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Role" field="role" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Backtests" field="backtestCount" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Optimizations" field="optimizationCount" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Paper Sessions" field="paperSessionCount" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Jobs" field="jobCount" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Failed Jobs" field="jobFailedCount" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Last Active" field="lastActiveAt" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((user, i) => (
              <tr
                key={user.userId}
                className={i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}
              >
                <td className="px-3 py-2 text-gray-100 font-medium">{user.username}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                    user.role === 'admin'
                      ? 'bg-purple-500/20 text-purple-300'
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-300 tabular-nums">{user.backtestCount.toLocaleString()}</td>
                <td className="px-3 py-2 text-gray-300 tabular-nums">{user.optimizationCount.toLocaleString()}</td>
                <td className="px-3 py-2 text-gray-300 tabular-nums">{user.paperSessionCount.toLocaleString()}</td>
                <td className="px-3 py-2 text-gray-300 tabular-nums">{user.jobCount.toLocaleString()}</td>
                <td className="px-3 py-2 tabular-nums">
                  <span className={user.jobFailedCount > 0 ? 'text-red-400' : 'text-gray-400'}>
                    {user.jobFailedCount.toLocaleString()}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-400 whitespace-nowrap">
                  {formatTimeAgo(user.lastActiveAt)}
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
